"use strict";

import extend from "lodash/extend";
import isArray from "lodash/isArray";
import isObject from "lodash/isObject";
import map from "lodash/map";
import mapValues from "lodash/mapValues";
import reduce from "lodash/reduce";

import ReactUtils from "@/legacy/ReactUtils.jsx";

const TransactionUtils = {};
export default TransactionUtils;

TransactionUtils.formatStepDescription = function (stepDef, short) {
  // a user-defined title replaces the description generated from the step template
  if (stepDef.title) {
    return ReactUtils.escapeHTML(stepDef.title);
  }
  if (stepDef.step_def === "C_IF") {
    var condCount = (stepDef.values.conditions || []).length;
    var thenCount = (stepDef.values.then_steps || []).length;
    var elseCount = (stepDef.values.else_steps || []).length;
    var desc = "IF (" + condCount + " condition" + (condCount !== 1 ? "s" : "") + ")";
    desc += " THEN " + thenCount + " step" + (thenCount !== 1 ? "s" : "");
    if (elseCount > 0) {
      desc += " ELSE " + elseCount + " step" + (elseCount !== 1 ? "s" : "");
    }
    return ReactUtils.escapeHTML(desc);
  }
  return reduce(
    stepDef.fields,
    function (string, field) {
      var regex = new RegExp("\\{" + field.name + "\\}", "g");
      var val = (stepDef.values[field.name] || field.name).toString();

      if (val !== field.name && TransactionUtils.isSecretTextField(stepDef, field)) {
        val = "*".repeat(val.length);
      }
      if (short && field.type === "SELECTOR") {
        //try to shorten value to single tag name
        try {
          const last = val.split(" ").slice(-1)[0].trim();
          if (last) {
            const parts = last.split(".");
            const tag = parts[0];
            let cssClass = "";
            if (parts.length > 1) {
              cssClass = parts[1].split(":")[0];
            }
            if (tag.indexOf("#") > -1) {
              val = "… " + tag;
            } else if (cssClass !== "") {
              val = "… " + tag + "." + cssClass;
            }
          }
        } catch (e) {
          return undefined;
        }
      }

      return string.replace(
        regex,
        '<span class="user-value pulseTextBody">' + ReactUtils.escapeHTML(val).replace(/\$/g, "$$$$") + "</span>"
      );
    },
    ReactUtils.escapeHTML(stepDef.template)
  );
};

TransactionUtils.formatStepDetails = function (stepDetails) {
  if (!stepDetails) {
    return null;
  }

  const details = mapValues(stepDetails, (val, key) => {
    if (isObject(val)) {
      val = map(val, function (v, k) {
        return k + ": " + v;
      });
    }

    if (isArray(val)) {
      if (val.length) {
        val = reduce(
          val,
          function (memo, v) {
            return (memo += `<span>${v}</span><br/>`);
          },
          ""
        );
      } else {
        val = null;
      }
    }

    if (key === "response_body" && val) {
      //escape html and special chars in response body
      val = val.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    }

    return `<dt><strong>${TransactionUtils.formatDetailsHeading(key)}</strong></dt><dd>${val || "<none>"}</dd>`;
  });

  let mergedDetails = "";
  for (const key in details) {
    mergedDetails += details[key];
  }
  return mergedDetails;
};

TransactionUtils.formatDetailsHeading = function (keyName) {
  keyName = keyName.replace(/_/g, " ");
  return keyName;
};

TransactionUtils.getFirstSentence = function (text) {
  return text.split(".")[0] + ".";
};

TransactionUtils._SECRET_REGEX = /API|TOKEN|KEY|SECRET|PASS|SIGNATURE|PW/i;
TransactionUtils.isSecretTextField = function (step, field) {
  return (
    step.step_def === "C_FILL_FIELD" &&
    field.name === "text" &&
    TransactionUtils._SECRET_REGEX.test(step.values.element)
  );
};

TransactionUtils.createEmptyStep = function (step_key) {
  return {
    step_def: step_key || "",
    title: "",
    values: {},
    errors: {},
    results: {
      response_time: -1,
      error: null,
    },
  };
};

/* Resets results on a step and all its nested sub-steps (IF branches). */
TransactionUtils.resetStepResults = function (step) {
  var reset = {response_time: -1, error: null};
  var newStep = extend({}, step, {results: reset});
  if (step.step_def === "C_IF") {
    newStep.values = extend({}, step.values, {
      conditions: map(step.values.conditions || [], function (s) {
        return extend({}, s, {results: reset});
      }),
      then_steps: map(step.values.then_steps || [], function (s) {
        return extend({}, s, {results: reset});
      }),
      else_steps: map(step.values.else_steps || [], function (s) {
        return extend({}, s, {results: reset});
      }),
    });
  }
  return newStep;
};

/*
 * After a test run, propagates the IF step's condition_results / branch_results onto the
 * sub-step objects so each one carries its own results and can show success/error styling.
 *
 * Both arrays are truncated by the checker at the first failure, so a sub-step with no
 * matching entry was never executed and keeps its reset state. The branch that was not
 * taken is left untouched for the same reason.
 */
TransactionUtils.propagateIfResults = function (step) {
  if (step.step_def !== "C_IF" || !step.results || !step.results.result) {
    return step;
  }
  var ifResult = step.results.result;
  var branchKey = ifResult.conditions_passed ? "then_steps" : "else_steps";

  function applyResults(subSteps, subResults) {
    if (!subSteps || !subResults) {
      return subSteps;
    }
    return map(subSteps, function (subStep, i) {
      if (i >= subResults.length) {
        return subStep;
      }
      var subResult = subResults[i];
      // a nested IF carries its own branch outcomes, so recurse to reach its sub-steps
      return TransactionUtils.propagateIfResults(
        extend({}, subStep, {
          results: {
            error: subResult.error || null,
            response_time: subResult.error ? -1 : 0,
            result: subResult.result || null,
          },
        })
      );
    });
  }

  var newValues = extend({}, step.values);
  newValues.conditions = applyResults(newValues.conditions, ifResult.condition_results);
  newValues[branchKey] = applyResults(newValues[branchKey], ifResult.branch_results);
  return extend({}, step, {values: newValues});
};

TransactionUtils.mergeStepDefsIntoScript = function (transactionScript, stepDefsMap) {
  return map(transactionScript, function (step) {
    var merged = extend(TransactionUtils.createEmptyStep(), step, stepDefsMap[step.step_def]);
    if (step.step_def === "C_IF") {
      merged.values = extend({}, step.values, {
        conditions: TransactionUtils.mergeStepDefsIntoScript(step.values.conditions || [], stepDefsMap),
        then_steps: TransactionUtils.mergeStepDefsIntoScript(step.values.then_steps || [], stepDefsMap),
        else_steps: TransactionUtils.mergeStepDefsIntoScript(step.values.else_steps || [], stepDefsMap),
      });
    }
    return merged;
  });
};
